import { supabase } from './supabase'

/* =====================================================================
   Typed wrappers over the admin RPCs in 0006_admin.sql.

   Every call here goes through a security-definer function that checks
   `pan_private.is_secretariat()` and raises 42501 otherwise. That is why the
   functions refuse loudly rather than returning an empty set: an empty
   verification queue and "you are not Secretariat" must not look the same to
   whoever is staring at this screen.
   ===================================================================== */

export type MemberStatus =
  | 'draft'
  | 'pending'
  | 'approved'
  | 'flagged'
  | 'rejected'
  | 'suspended'

export interface AdminStats {
  pendingRequests: number
  approvedThisWeek: number
  approvedLastWeek: number
  flagged: number
  totalOnRegister: number
  countries: number
  avgDecisionDays: number | null
}

export interface RequestRow {
  id: string
  kit_no: string
  official_name: string
  house: string
  passing_year: number | null
  email: string
  status: MemberStatus
  submitted_at: string | null
  duplicate_of: string | null
  duplicate_kit_no: string | null
  duplicate_name: string | null
  total: number
}

export interface AuditRow {
  id: string
  actor_name: string | null
  action: string
  target_kit_no: string | null
  target_name: string | null
  before_state: Record<string, unknown> | null
  after_state: Record<string, unknown> | null
  note: string | null
  created_at: string
  total: number
}

export interface HouseCount {
  house: string
  member_count: number
}

/** Postgres error codes we translate into something a human can act on. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function translate(error: { message: string; code?: string }): ApiError {
  if (error.code === '42501') {
    return new ApiError(
      'This account is not a Secretariat user. Sign in with a Secretariat account.',
      error.code,
    )
  }
  if (error.code === 'P0002') {
    return new ApiError('That record no longer exists.', error.code)
  }
  if (error.code === '22023') {
    return new ApiError(error.message, error.code)
  }
  return new ApiError(error.message, error.code)
}

async function rpc<T>(fn: string, params?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, params)
  if (error) throw translate(error)
  return data as T
}

/* ---- Reads --------------------------------------------------------- */

export const getStats = () => rpc<AdminStats>('admin_stats')

export const getRequests = (opts: {
  status?: MemberStatus | null
  q?: string | null
  page?: number
  perPage?: number
}) =>
  rpc<RequestRow[]>('admin_requests', {
    p_status: opts.status ?? null,
    p_q: opts.q && opts.q.trim() !== '' ? opts.q.trim() : null,
    p_page: opts.page ?? 1,
    p_per_page: opts.perPage ?? 20,
  })

export const getAudit = (
  opts: {
    actor?: string | null
    from?: string | null
    to?: string | null
    page?: number
    perPage?: number
  } = {},
) =>
  rpc<AuditRow[]>('admin_audit', {
    p_actor: opts.actor ?? null,
    // Dates arrive as YYYY-MM-DD from <input type="date">. The `to` bound is
    // pushed to the end of that day, or a same-day filter would match nothing.
    p_from: opts.from ? `${opts.from}T00:00:00Z` : null,
    p_to: opts.to ? `${opts.to}T23:59:59Z` : null,
    p_page: opts.page ?? 1,
    p_per_page: opts.perPage ?? 50,
  })

export const getHouseCounts = () => rpc<HouseCount[]>('houses_with_counts')

/**
 * The full submission, for the detail panel. Secretariat callers get the
 * unredacted record — `serialise_member` returns everything when
 * `is_secretariat()` holds — which is the whole point: you cannot check a
 * record against the CCP file if the member's privacy settings hide half of it.
 */
export const getSubmission = (kitNo: string) =>
  rpc<Record<string, unknown> | null>('member_profile', { p_kit_no: kitNo })

/* ---- Decisions ----------------------------------------------------- */

export const approve = (id: string, note?: string) =>
  rpc<{ id: string; status: MemberStatus }>('admin_approve', {
    p_member_id: id,
    p_note: note ?? null,
  })

/** The server requires a reason and emails it to the applicant. */
export const reject = (id: string, reason: string) =>
  rpc<{ id: string; status: MemberStatus }>('admin_reject', {
    p_member_id: id,
    p_reason: reason,
  })

export const flag = (id: string, reason: string) =>
  rpc<{ id: string; status: MemberStatus }>('admin_flag', {
    p_member_id: id,
    p_reason: reason,
  })

/**
 * Bulk approve. The server refuses flagged or duplicate records and reports
 * which ones it skipped, so the UI can say so rather than implying success.
 */
export const bulkApprove = (ids: string[]) =>
  rpc<{ approved: string[]; skipped: string[]; skippedReason: string }>(
    'admin_bulk_approve',
    { p_ids: ids },
  )

/** Records the intent and returns the audit row id. Used on its own only as a fallback. */
export const logExport = (note?: string) =>
  rpc<string>('admin_log_export', { p_note: note ?? null })

/**
 * Runs the real export through the `export-csv` Edge Function.
 *
 * The function verifies the caller with their own token, writes the audit row,
 * assembles the file with elevated privileges and returns a signed URL that
 * expires in 15 minutes. The full record set — including contact details members
 * hide from each other — is never assembled in the browser.
 */
export async function exportRegister(opts: {
  status?: MemberStatus | null
  note?: string
} = {}): Promise<{ url: string; rows: number; expiresInSeconds: number }> {
  const { data, error } = await supabase.functions.invoke('export-csv', {
    body: { status: opts.status ?? null, note: opts.note ?? 'Register export' },
  })

  if (error) {
    const details = (error as { context?: { error?: { message?: string } } }).context
    throw new ApiError(details?.error?.message ?? error.message)
  }
  if (!data || typeof data.url !== 'string') {
    throw new ApiError('The export did not return a download link.')
  }
  return {
    url: data.url as string,
    rows: Number(data.rows) || 0,
    expiresInSeconds: Number(data.expiresInSeconds) || 900,
  }
}


/* ---- Member register (0010) ---------------------------------------- */

export interface RegisterRow {
  id: string
  kit_no: string
  official_name: string
  house: string
  passing_year: number | null
  email: string
  cell_number: string
  current_organization: string | null
  current_position: string | null
  city: string | null
  country: string | null
  status: MemberStatus
  visibility: 'public' | 'petarians_only' | 'private'
  hidden_field_count: number
  completeness: number
  decided_at: string | null
  deletion_requested_at: string | null
  suspension_reason: string | null
  deletion_reason: string | null
  deletion_requested_by: string | null
  total: number
}

export const getRegister = (opts: {
  q?: string | null
  status?: MemberStatus | null
  house?: string | null
  page?: number
  perPage?: number
}) =>
  rpc<RegisterRow[]>('admin_members', {
    p_q: opts.q && opts.q.trim() !== '' ? opts.q.trim() : null,
    p_status: opts.status ?? null,
    p_house: opts.house ?? null,
    p_page: opts.page ?? 1,
    p_per_page: opts.perPage ?? 25,
  })

/**
 * Secretariat correction. The server enforces a column allow-list, so status,
 * visibility, hidden_fields and kit_no are rejected with 42501 — those belong to
 * the decision functions and to the member respectively.
 */
export const updateMember = (
  id: string,
  patch: Record<string, string | null>,
  note?: string,
) =>
  rpc<{ id: string; status: MemberStatus; officialName: string }>(
    'admin_update_member',
    { p_member_id: id, p_patch: patch, p_note: note ?? null },
  )

/* ---- Holding and removing a member (0033/0034) ----------------------
 *
 * Everything above acts on a record that has not been decided yet. These four
 * are the ones that act on a member who is already on the register — the gap
 * that meant an approved record could only ever be corrected, never held and
 * never removed.
 */

/**
 * Put an approved member's access on hold. Reversible, and the record is left
 * intact. The server refuses anything that is not currently approved, and
 * requires a reason because the member is told it.
 */
export const suspend = (id: string, reason: string) =>
  rpc<{ id: string; status: MemberStatus }>('admin_suspend', {
    p_member_id: id,
    p_reason: reason,
  })

export const reinstate = (id: string) =>
  rpc<{ id: string; status: MemberStatus }>('admin_reinstate', {
    p_member_id: id,
  })

/**
 * Start the deletion clock on a member's behalf — for the person who rings up
 * and asks to be taken off, or who has lost the address they registered with
 * and so cannot do it themselves.
 *
 * Not a delete. It sets the same column the member's own request sets, so the
 * thirty-day grace and the nightly purge do the work, and `cancelDeletion`
 * can undo it for as long as that grace lasts.
 */
export const requestDeletion = (id: string, reason: string) =>
  rpc<{ id: string; deletionRequestedAt: string; purgesAfter: string }>(
    'admin_request_deletion',
    { p_member_id: id, p_reason: reason },
  )

export const cancelDeletion = (id: string) =>
  rpc<{ id: string; deletionRequestedAt: null }>('admin_cancel_deletion', {
    p_member_id: id,
  })

/* ---- Communications (0010) ----------------------------------------- */

export type Audience = 'all' | 'house' | 'passingYearRange'

export interface AnnouncementRow {
  id: string
  title: string
  body: string
  audience: Audience
  audience_house: string | null
  audience_year_from: number | null
  audience_year_to: number | null
  published_at: string | null
  author_name: string | null
  total: number
}

export const getAnnouncements = (opts: { page?: number; perPage?: number } = {}) =>
  rpc<AnnouncementRow[]>('admin_announcements', {
    p_page: opts.page ?? 1,
    p_per_page: opts.perPage ?? 25,
  })

export const publishAnnouncement = (a: {
  title: string
  body: string
  audience: Audience
  house?: string | null
  yearFrom?: number | null
  yearTo?: number | null
  publishNow?: boolean
}) =>
  rpc<{ id: string; publishedAt: string | null; recipients: number }>(
    'admin_publish_announcement',
    {
      p_title: a.title,
      p_body: a.body,
      p_audience: a.audience,
      p_house: a.house ?? null,
      p_year_from: a.yearFrom ?? null,
      p_year_to: a.yearTo ?? null,
      p_publish_now: a.publishNow ?? true,
    },
  )

/* ---- Portal settings & memorial roll (0011) ------------------------ */

export interface StaffRow {
  id: string
  name: string
  designation: string | null
  email: string
  role: 'secretariat_staff' | 'secretariat_admin'
  mfa_enabled: boolean
  last_login_at: string | null
  created_at: string
  is_self: boolean
}

export const getStaff = () => rpc<StaffRow[]>('admin_staff_list')

/** The server refuses self-demotion and refuses to leave zero admins. */
export const setStaffRole = (id: string, role: StaffRow['role']) =>
  rpc<{ id: string; role: string }>('admin_set_staff_role', {
    p_id: id,
    p_role: role,
  })

export const removeStaff = (id: string) =>
  rpc<{ id: string; removed: boolean }>('admin_remove_staff', { p_id: id })

/**
 * Invites a Secretariat account via the `invite-secretariat` Edge Function.
 *
 * Cannot be a plain RPC: creating the auth user needs the service-role key,
 * which must never be in this bundle. The function re-checks that the caller is
 * an admin using the caller's own token.
 */
export async function inviteStaff(input: {
  email: string
  name: string
  designation?: string
  role: 'secretariat_staff' | 'secretariat_admin'
}): Promise<{ id: string; email: string; message: string }> {
  const { data, error } = await supabase.functions.invoke('invite-secretariat', {
    body: input,
  })

  if (error) {
    const details = (error as { context?: { error?: { message?: string } } }).context
    throw new ApiError(details?.error?.message ?? error.message)
  }
  if (!data?.id) throw new ApiError('The invitation did not complete.')
  return data as { id: string; email: string; message: string }
}

export interface HealthCheck {
  exportsBucket: 'ok' | 'missing' | 'unknown'
  cronJobs: string[] | 'unknown'
  admins: number
  queueStuck: number
  queueOldestMinutes: number | null
  pendingRequests: number
  erasureDue: number
}

export const getHealth = () => rpc<HealthCheck>('admin_health_check')

export const importMartyrs = (rows: Record<string, string>[]) =>
  rpc<{ imported: number; skipped: number; problems: string[] }>(
    'admin_import_martyrs',
    { p_rows: rows },
  )

export interface NotificationStats {
  queued: number
  sent: number
  failed: number
  stuck: number
  oldestQueuedAt: string | null
}

export const getNotificationStats = () =>
  rpc<NotificationStats>('admin_notification_stats')

export interface MartyrRow {
  id: string
  kit_no: string | null
  full_name: string
  house: string | null
  passing_year: number | null
  rank_title: string | null
  unit: string | null
  date_of_death: string | null
  place: string | null
  citation: string | null
  photo_url: string | null
}

export const getMartyrs = () => rpc<MartyrRow[]>('martyrs_roll')

export const upsertMartyr = (id: string | null, data: Record<string, string>) =>
  rpc<{ id: string; fullName: string }>('admin_upsert_martyr', {
    p_id: id,
    p_data: data,
  })

export const HOUSES = [
  'Jinnah',
  'Liaquat',
  'Ayub',
  'Latif',
  'Iqbal',
  'Muhammad Bin Qasim',
  'Shahbaz',
  'Sachal',
] as const

/* ---- Formatting helpers -------------------------------------------- */


export function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${formatDate(iso)}, ${d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

/** "3 days ago" — how long a submission has been waiting. */
export function waitingFor(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day'
  return `${days} days`
}
